import type { MixStep } from "@/lib/api/ai-refill";

// RecipeOverview renders the engine's proposed mix as an ordered, glanceable
// list: nutrient, target dose, signature, safety flags, and gate rules. It's
// shared by step 4 (Proposed mix — plain review) and step 5 (Mixing wizard —
// live guidance). When `dosedCount` is provided, the first N steps render as
// done (✓, dimmed) and step N as the current one (highlighted), so the grower
// always sees the full recipe and exactly where they are in it.
export function RecipeOverview({
  plan,
  dosedCount,
}: {
  plan: MixStep[];
  dosedCount?: number;
}) {
  const guided = dosedCount !== undefined;
  return (
    <ol className="space-y-2">
      {plan.map((m, i) => {
        const done = guided && i < dosedCount!;
        const current = guided && i === dosedCount!;
        return (
          <li
            key={m.order_rank}
            className={
              "rounded-md border px-3 py-2 text-sm " +
              (current
                ? "border-primary/60 bg-primary/5"
                : done
                  ? "opacity-60"
                  : "")
            }
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium">
                {done && "✓ "}
                {current && "→ "}
                {m.order_rank}. {m.nutrient_name}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {m.amount_ml.toFixed(1)} mL ({m.ml_per_gal} mL/gal)
              </span>
            </div>
            <MixFlags m={m} />
            {m.gate_rules.length > 0 && (
              <ul
                className={
                  "mt-1 space-y-0.5 text-xs " +
                  (current
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-muted-foreground")
                }
              >
                {m.gate_rules.map((g, j) => (
                  <li key={j}>
                    {current ? "⚠ " : ""}gate: if {g.if} → {g.then}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// MixFlags surfaces a nutrient's safety-relevant traits so the grower can see
// (and override) why the engine ordered it where it did.
export function MixFlags({ m }: { m: MixStep }) {
  const flags: string[] = [];
  if (m.contains_calcium) flags.push("calcium");
  if (m.contains_phosphate) flags.push("phosphate");
  if (m.is_biological) flags.push("biological");
  if (m.is_ph_adjuster) flags.push("pH adjuster");
  if (flags.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f}
          className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
        >
          {f}
        </span>
      ))}
    </div>
  );
}
